<template>
	<div class="sidebar">
		<div class="sidebar__group sidebar__group--title-only">
			<h4 class="sidebar__title">Content</h4>
		</div>
		<div class="sidebar__step sidebar__step--intro"  v-bind:class="{'sidebar__step--active': activeStep === null}">
			<a @click="activateIntro()" v-tooltip:left="introErrorTooltip">
				Tutorial details
				<span v-if="stepErrors.intro" class="step-error">
					<i class="fas fa-exclamation-circle"></i>
				</span>
			</a>
		</div>
		<draggable v-model="steps" :options="{handle: '.sidebar__step__handle'}">
			<transition-group name="list-reorder" tag="ul" class="sidebar__list">
				<li v-for="(step, stepindex) in steps" :key="stepindex" class="sidebar__step" v-bind:class="{'sidebar__step--active': stepindex === activeStepIndex}">
					<span class="sidebar__step__handle" v-if="steps.length > 1"></span>
					<a @click="activateStep(step)">Step {{stepindex + 1}}<span v-if="step.title">:</span> {{step.title}}</a>
					<span class="sidebar__step__delete fas fa-trash-alt" @click="() => confirmDeleteStep(stepindex)" v-if="steps.length > 1"></span>
				</li>
			</transition-group>
		</draggable>
		<div class="sidebar__group sidebar__add-step--container position-relative">
			<persistent-tooltip :content="tooltips.addStep.content" @remove="nextTip" v-if="tipsShown && tooltips.addStep.active" class="create-tutorial-tooltip" placement="left"></persistent-tooltip>
			<a href="#" class="sidebar__add-step" v-on:click.prevent="addStep">
				+ New step
			</a>
		</div>
		<transition name="delete-transition" mode="out-in">
			<div class="sidebar__group app-separator--top" v-if="!isNew">
				<h4 class="sidebar__title">Delete</h4>
				<a class="delete-link" @click="confirmDeleteTutorial">
					Delete tutorial
				</a>
			</div>
		</transition>
		<confirm-modal :condition="deleteModalActive" @cancel="clearStepModal" @ok="deleteStep">
			<template slot="messageTitle">Delete step</template>
			<template slot="messageMain">Are you sure you want to delete this step and all of its contents (including images)?</template>
			<template slot="messageInfo">This cannot be undone.</template>
			<template slot="confirmText">Delete step</template>
		</confirm-modal>
		<confirm-modal :condition="deleteTutorialModalActive" @cancel="clearDeleteTutorialModal" @ok="deleteTutorial">
			<template slot="messageTitle">Delete tutorial</template>
			<template slot="messageMain">Are you sure you want to delete this tutorial and all of its contents (including images)?</template>
			<template slot="messageInfo">This cannot be undone.</template>
			<template slot="confirmText">Delete tutorial</template>
		</confirm-modal>
	</div>
</template>

<script>
	import { mapMutations, mapState, mapActions, mapGetters } from 'vuex'
	import draggable from 'vuedraggable'
	import ConfirmModal from './ConfirmModal'
	import PersistentTooltip from './PersistentTooltip'
	import { tooltips } from '@/tooltips/tutorial_editor'

	export default {
		name: 'app-sidebar',
		components: {
			draggable,
			ConfirmModal,
			PersistentTooltip
		},
		data () {
			return {
				deleteTutorialModalActive: false,
				deleteModalActive: false,
				stepToBeDeletedIndex: null
			}
		},
		computed: {
			...mapState({
				stepErrors: 'stepErrors',
				activeStep: 'activeStep',
				tooltips: 'tooltips'
			}),
			...mapGetters(['activeStepIndex', 'tipsShown', 'isNew']),
			steps: {
				get () {
					return this.$store.state.tutorial.steps
				},
				set (value) {
					this.$store.commit('updateSteps', value)
				}
			},
			introErrorTooltip () {
				return this.stepErrors.intro ? tooltips.stepErrors.intro : false
			}
		},
		methods: {
			...mapMutations({
				activateIntro: 'activateIntro',
				setSteps: 'setSteps',
				nextTip: 'nextTip'
			}),
			...mapActions(['activateStep', 'addStep']),
			deleteStep () {
				this.$store.dispatch('deleteStep', this.stepToBeDeletedIndex)
				this.clearStepModal()
			},
			confirmDeleteStep (index) {
				this.deleteModalActive = true
				this.stepToBeDeletedIndex = index
			},
			clearStepModal () {
				this.deleteModalActive = false
				this.stepToBeDeletedIndex = null
			},
			deleteTutorial () {
				this.$store.dispatch('deleteTutorial')
				this.clearDeleteTutorialModal()
			},
			confirmDeleteTutorial () {
				this.deleteTutorialModalActive = true
			},
			clearDeleteTutorialModal () {
				this.deleteTutorialModalActive = false
			},

			stepActiveClass (stepindex) {
				return {
					'sidebar__step--active': () => stepindex === this.activeStepIndex
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import "@/styles/tutorial_editor/globals";

	.sidebar {
		&__title {
			margin: 0;
			color: #b3b3b3;
			font-size: .750em;
			padding: $tutorial-editor-sidebar-padding 0 0;
			text-transform: uppercase;
		}

		&__list {
			margin: 0;
			list-style: none;
			padding: 0;
		}

		&__step {
			position: relative;
			font-size: 0.875rem;
			border: 1px solid transparent;

			> a {
				display: block;
				padding: 10px $tutorial-editor-sidebar-padding;
				cursor: pointer;
				background-color: transparent;
				width:100%;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
			&, & > a {
				transition: all 0.2s $transition-curve;
			}

			&:hover, &--active {
				background-color: $snow-white;
			}


			&:hover {
				.sidebar__step__handle, .sidebar__step__delete {
					opacity: 1;
				}
			}

			&__handle, &__delete {
				transition: opacity 0.3s $transition-curve;
				position: absolute;
			}

			&__delete {
				right: 0;
				top:0;
				bottom:0;
				position: absolute;
				color: $mid-gray;
				opacity:0;
				font-size: 16px;
				cursor: pointer;

				&:before {
					position: absolute;
					padding-left:10px;
					padding-right:10px;
					right:0;
					top: calc(50% - 9px);
				}
			}

			&__handle {
				left: 0px;
				z-index:1;
				opacity: 0;
				position: absolute;
				top: 0;
				bottom:0;
				display:block;
				width: 28px;
				cursor: grab;
				&:before {
					top: calc(50% - 3px);
				}
				&:after {
					bottom: calc(50% - 3px);
				}
				&:before, &:after {
					content: " ";
					background-color: $mid-gray;
					height: 2px;
					left: 7px;
					right: 7px;
					position: absolute;
				}
			}

			&--intro {
				margin-top: 12px;
			}
		}

		&__group {
			padding: 0 $tutorial-editor-sidebar-padding;

			&:not(.sidebar__group--title-only) {
				padding-bottom: $tutorial-editor-sidebar-padding;
			}
		}

		&__add-step {
			color: $blue-alt;
			font-weight: bold;
			text-decoration: none;
			display:inline-block;
			padding: 12px 12px;
			margin-left: -12px;
			font-size: 0.875rem;

			&:hover {
				text-decoration: underline;
			}

			&--container {
				margin-top:0;
				position: relative;
			}
		}
	}

	.step-error { color: $red; }

	.delete-link {
		color: $red;
		font-size: 0.875rem;
		margin-top: 10px;
		display: inline-block;
		cursor: pointer;
		&:hover {
			text-decoration: underline;
		}
	}
	.create-tutorial-tooltip {
		position: absolute;
		top: 22px;
	}
</style>
